import colorCodes from "../config/colorCodes";
import { GaugeData } from "../domain/types";

const calculateData = (operatingRange: GaugeData, noOfParts: number) => {
  const { minValue, maxValue, value, minRange, maxRange } = operatingRange;
  const operatingPoints = [minValue, minRange, maxRange, maxValue];

  // Helper: compute differences between consecutive points
  const computeDifferences = (arr: (number | null)[]) =>
    arr.slice(0, -1).map((v, i) => {
      const next = arr[i + 1];
      return v != null && next != null ? Math.abs(next - v) : 0;
    });

  const differences = computeDifferences(operatingPoints);
  const totalValue = differences.reduce((acc, v) => acc + v, 0);
  const eachPart = totalValue / noOfParts;

  // Helper: mark edges
  const markEdges = (arr: string[], target = "#f95460", mark = "#ffb554") => {
    const n = arr.length;
    const toMark = new Set<number>();

    for (let i = 0; i < n - 1; i++) {
      if (arr[i] === target && arr[i + 1] !== target) {
        for (let k = 0, idx = i; k < 4 && idx >= 0; k++, idx--) toMark.add(idx);
      }
      if (arr[i] !== target && arr[i + 1] === target) {
        for (let k = 0, idx = i + 1; k < 6 && idx < n; k++, idx++) toMark.add(idx);
      }
    }

    return arr.map((v, idx) => (toMark.has(idx) ? mark : v));
  };

  // Build operating range dataset
  const rangeSet = differences.reduce(
    (acc, diff, idx) => {
      const times = Math.floor((diff / totalValue) * noOfParts);
      const color = idx === 0 || idx === 2 ? colorCodes.red : colorCodes.green;

      for (let i = 0; i < times; i += 2) {
        acc.data.push(eachPart, eachPart);
        acc.bg.push(color, color);
      }

      return acc;
    },
    { data: [] as number[], bg: [] as string[] }
  );

  rangeSet.bg = markEdges(rangeSet.bg);

  // Actual value dataset
  const actualValueDataSet = { data: [0], bg: [""], shadowBg: [""] };
  let zone = "green";
  let defaultDataSet = true;

  if (value != null && minValue != null && maxValue != null && minRange != null && maxRange != null) {
    let occupyFull = false;

    if (value >= minRange && value <= maxRange) zone = "green";
    else if (value >= minValue && value <= maxValue) zone = "red";
    else if (value > maxValue) {
      zone = "red";
      occupyFull = true;
    }

    if (occupyFull) {
      defaultDataSet = false;
      actualValueDataSet.data = [totalValue];
      actualValueDataSet.bg = [colorCodes[zone]];
      actualValueDataSet.shadowBg = [colorCodes[`${zone}Shadow`]];
    } else {
      defaultDataSet = false;
      let zoneValue = value - minValue;
      if (minValue < 0) zoneValue = value === 0 ? Math.abs(minValue) : Math.abs(value - minValue);
      actualValueDataSet.data = [zoneValue, totalValue - zoneValue];
      actualValueDataSet.bg = [colorCodes[zone], colorCodes.white];
      actualValueDataSet.shadowBg = [colorCodes[`${zone}Shadow`], "transparent"];
    }
  }

  const disable = operatingPoints.some((v) => v == null) || value == null;

  return {
    totalValue,
    rangeSet,
    valueSet: actualValueDataSet,
    disable,
    defaultDataSet: !defaultDataSet ? 0 : 1,
    inRange: zone === "green",
  };
};

export default calculateData;
